import React, { useState, useEffect, useMemo } from 'react'
import {
  Table,
  Tag,
  Space,
  Segmented,
  DatePicker,
  Input,
  Typography,
  Statistic,
  Row,
  Col,
  Modal,
  Descriptions,
  Divider,
  Spin,
  Button,
  Empty,
  Flex,
} from 'antd'
import {
  SearchOutlined,
  EyeOutlined,
  ReloadOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
  WalletOutlined,
  CalendarOutlined,
  PrinterOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import BillService from '../../services/Billservice'
import { generateBillPdf } from './billPdfGenerator'
import { notify } from '../Customs/NotifyProvider'
import "./billModal.css";

const { Text, Title } = Typography
const { RangePicker } = DatePicker

dayjs.extend(utc)
dayjs.extend(timezone)

const formatBillDateTime = (value, format = 'DD MMM YYYY, hh:mm A') => {
  if (!value) return '-'

  return dayjs(value).format(format)
}

const HOTEL_INFO = {
  name: 'Nityanand',
  address: 'Shop No-5, Shukla Estate, S.V Road Oshiwara, Opp Ajit Glass Jogeshwari(W), Mumbai 400102',
  phone: '',
  gst: '',
}

// ─── Parse payment_summary JSON string safely ────────────────────────────────
const parsePaymentSummary = (raw) => {
  if (!raw) return null
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw
  } catch {
    return null
  }
}

// ─── Fallback calc from items (used for list-level totals only) ──────────────

 const getBillTotal = (bill,generalSettings) => {
  const ps = parsePaymentSummary(bill.payment_summary || bill.bill_summary) || {};


  const foodCgst = Number(ps?.food_cgst ?? bill?.food_cgst ?? 0)

  const foodSgst = Number(ps?.food_sgst ?? bill?.food_sgst ?? 0)

  const drinkCgst = Number(ps?.drink_cgst ?? bill?.drink_cgst ?? bill?.totals?.drink_cgst ?? 0)

  const drinkSgst = Number(ps?.drink_sgst ?? bill?.drink_sgst ?? bill?.totals?.drink_sgst ?? 0)

  const drinkVat = Number(ps?.drink_vat ?? bill?.drink_vat ?? bill?.totals?.drink_vat ?? 0)


  if (Object.keys(ps).length > 0) {

 const items = Array.isArray(bill.items) ? bill.items : []

const parcelQty = items.reduce((sum, i) => {
  if (Number(i.is_parcel) === 1) {
    return sum + Number(i.qty || 1)
  }
  return sum
}, 0)

const parcelCharge =
  parcelQty * Number(generalSettings?.parcel_charge || 0)


  const acCharge =
  bill?.zone === 'AC'
    ? Number(generalSettings?.ac_charge || 0)
    : Number(
        ps?.ac_charge ??
        ps?.acCharge ??
        bill?.ac_charge ??
        bill?.acCharge ??
        0
      )

  return {
    subtotal: (ps.food_subtotal || 0) + (ps.drink_subtotal || 0),

    discount: ps.food_discount || 0,

    tax:
      (ps.food_cgst || 0) +
      (ps.food_sgst || 0) +
      (ps.drink_cgst || 0) +
      (ps.drink_sgst || 0) +
      ((ps.drink_vat ?? bill?.totals?.drink_vat) || 0),

   total:
  (
    Number(ps.grand_total || 0) +
    parcelCharge +
    acCharge
  ) ||
  Number(bill?.totals?.total) ||
  (
    (ps.food_subtotal || 0) +
    (ps.drink_subtotal || 0) +
    parcelCharge +
    acCharge -
    (ps.food_discount || 0) +
    (
      (ps.food_cgst || 0) +
      (ps.food_sgst || 0) +
      (ps.drink_cgst || 0) +
      (ps.drink_sgst || 0) +
      (ps.drink_vat || 0)
    )
  ),

    fromSummary: true,
  };
}

  const items = Array.isArray(bill.items) ? bill.items : []

//separating drink and food subtotal

const foodSubtotalFromItems = items.reduce((sum, i) => {
  if (Number(i.is_drink || 0) === 1) return sum
  return sum + Number(i.price || 0) * Number(i.qty || 0)
}, 0)

const drinkSubtotalFromItems = items.reduce((sum, i) => {
  if (Number(i.is_drink || 0) === 1 && i.drink_details) {
    return sum + getDrinkTotal(i.drink_details)
  }
  return sum
}, 0)



  const subtotal = items.reduce((s, i) => {
    if (Number(i.is_drink || 0) === 1 && i.drink_details) {
      return s + getDrinkTotal(i.drink_details)
    }
    return s + Number(i.price || 0) * Number(i.qty || 0)
  }, 0)

  const discount = Number(bill.discount || 0)
  const foodTaxFromRows = items.reduce(
    (sum, i) => sum + Number(i.item_cgst_amount || 0) + Number(i.item_sgst_amount || 0),
    0
  )
  const drinkTaxFromRows = items.reduce(
    (sum, i) =>
      sum +
      Number(i.drink_cgst_amount || 0) +
      Number(i.drink_sgst_amount || 0) +
      Number(i.drink_vat_amount || 0),
    0
  )
  const tax = foodTaxFromRows + drinkTaxFromRows || Number(bill.tax || 0)

  const parcelCharge = Number(
  bill?.parcel_charge ??
  bill?.parcelCharge ??
  0
)

const acCharge = Number(
  bill?.ac_charge ??
  bill?.acCharge ??
  0
)

  return {
    subtotal,
    discount,
    tax,
    
   total: subtotal + parcelCharge + acCharge - discount + tax,  
     fromSummary: false,
  };
};

const getPaymentColor = (method) => {
  const map = { Cash: 'green', UPI: 'blue', Card: 'purple', Wallet: 'orange' }
  return map[method] || 'default'
}

// ─── Compute drink line-item total from drink_details ────────────────────────
const getDrinkTotal = (drink_details) => {
  if (!drink_details) return 0
  const { pegs = [], peg_qtys = {}, bottle_qty = 0, price_bottle = 0 } = drink_details
  const bottleTotal = Number(bottle_qty || 0) * Number(price_bottle || 0)
  const pegTotal = pegs.reduce((sum, peg) => {
    const orderedQty = Number(peg_qtys[String(peg.id)] || 0)
    return sum + orderedQty * Number(peg.price || 0)
  }, 0)
  return bottleTotal + pegTotal
}

// ─── Render drink detail lines (bottle + pegs) ───────────────────────────────
const DrinkDetailCell = ({ drink_details }) => {
  if (!drink_details) return <Text type="secondary">-</Text>
  const { pegs = [], peg_qtys = {}, bottle_qty = 0, price_bottle = 0 } = drink_details
  const lines = []

  if (bottle_qty > 0) {
    lines.push(
      <div key="bottle" style={{ fontSize: 12, color: 'var(--ant-color-text-secondary, #888)' }}>
        🍾 {bottle_qty} bottle × ₹{Number(price_bottle).toFixed(2)}
        <span style={{ marginLeft: 6, fontWeight: 600, color: 'var(--ant-color-text, #222)' }}>
          = ₹{(bottle_qty * Number(price_bottle)).toFixed(2)}
        </span>
      </div>
    )
  }

  pegs.forEach((peg) => {
    const qty = Number(peg_qtys[String(peg.id)] || 0)
    if (qty > 0) {
      lines.push(
        <div key={`peg-${peg.id}`} style={{ fontSize: 12, color: 'var(--ant-color-text-secondary, #888)' }}>
          🥃 {qty} peg ({peg.qty}ml) × ₹{Number(peg.price).toFixed(2)}
          <span style={{ marginLeft: 6, fontWeight: 600, color: 'var(--ant-color-text, #222)' }}>
            = ₹{(qty * Number(peg.price)).toFixed(2)}
          </span>
        </div>
      )
    }
  })

  return lines.length > 0 ? <div>{lines}</div> : <Text type="secondary">-</Text>
}

// ─── Modal ───────────────────────────────────────────────────────────────────
const BillDetailModal = ({ bill, open, onClose, generalSettings = {} }) => {
  const [printingDuplicate, setPrintingDuplicate] = useState(false)

  if (!bill) return null

  const ps = parsePaymentSummary(bill.payment_summary || bill.bill_summary)

  const { subtotal, discount } = getBillTotal(bill,generalSettings )
  const items = Array.isArray(bill?.items) ? bill.items : []
  //separating food and drink subtotal
  const foodSubtotalFromItems = items.reduce((sum, i) => {
  if (Number(i.is_drink || 0) === 1) return sum
  return sum + Number(i.price || 0) * Number(i.qty || 0)
}, 0)

const drinkSubtotalFromItems = items.reduce((sum, i) => {
  if (Number(i.is_drink || 0) === 1 && i.drink_details) {
    return sum + getDrinkTotal(i.drink_details)
  }
  return sum
}, 0)


  const foodCgstFromItems = items.reduce((s, i) => s + Number(i.item_cgst_amount || 0), 0)
  const foodSgstFromItems = items.reduce((s, i) => s + Number(i.item_sgst_amount || 0), 0)
  const drinkCgstFromItems = items.reduce((s, i) => s + Number(i.drink_cgst_amount || 0), 0)
  const drinkSgstFromItems = items.reduce((s, i) => s + Number(i.drink_sgst_amount || 0), 0)
  const drinkVatFromItems = items.reduce((s, i) => s + Number(i.drink_vat_amount || 0), 0)
  const drinkCgstFromPct = items.reduce((s, i) => {
    if (Number(i.is_drink || 0) !== 1) return s
    const lineTotal = i.drink_details ? getDrinkTotal(i.drink_details) : Number(i.price || 0) * Number(i.qty || 0)
    const pct = Number(i.drink_cgst_pct || 0) / 100
    return s + lineTotal * pct
  }, 0)
  const drinkSgstFromPct = items.reduce((s, i) => {
    if (Number(i.is_drink || 0) !== 1) return s
    const lineTotal = i.drink_details ? getDrinkTotal(i.drink_details) : Number(i.price || 0) * Number(i.qty || 0)
    const pct = Number(i.drink_sgst_pct || 0) / 100
    return s + lineTotal * pct
  }, 0)
 const drinkVatFromPct = items.reduce((sum, i) => {
  if (Number(i.is_drink || 0) !== 1) return sum

  const lineTotal = i.drink_details
    ? getDrinkTotal(i.drink_details)
    : Number(i.price || 0) * Number(i.qty || 0)

  const vatPct = Number(i.drink_vat_pct || 0)

  return sum + (lineTotal * vatPct) / 100
}, 0)

    //parcel and ac charge
// parcel and ac charge
const parcelQty = items.reduce((sum, i) => {
  if (Number(i.is_parcel) === 1) {
    return sum + Number(i.qty || 1)
  }
  return sum
}, 0)

const parcelCharge =
  parcelQty * Number(generalSettings?.parcel_charge || 0)

const acCharge =
  bill?.zone === 'AC'
    ? Number(generalSettings?.ac_charge || 0)
    : Number(
        ps?.ac_charge ??
        ps?.acCharge ??
        bill?.ac_charge ??
        bill?.acCharge ??
        0
      )




  const foodCgst = Number(ps?.food_cgst ?? bill?.food_cgst ?? bill?.totals?.food_cgst ?? (foodCgstFromItems || 0))
  const foodSgst = Number(ps?.food_sgst ?? bill?.food_sgst ?? bill?.totals?.food_sgst ?? (foodSgstFromItems || 0))
  const drinkCgst = Number(ps?.drink_cgst ?? bill?.drink_cgst ?? bill?.totals?.drink_cgst ?? (drinkCgstFromItems || drinkCgstFromPct || 0))
  const drinkSgst = Number(ps?.drink_sgst ?? bill?.drink_sgst ?? bill?.totals?.drink_sgst ?? (drinkSgstFromItems || drinkSgstFromPct || 0))
  const drinkSubtotalResolved = Number(ps?.drink_subtotal ?? bill?.drink_subtotal ?? bill?.totals?.drink_subtotal ?? drinkSubtotalFromItems ?? 0)
  const drinkVatPct = Number(generalSettings?.drink_vat || 0)
  const drinkVatFromSettings = (drinkSubtotalResolved * drinkVatPct) / 100
  const drinkVat = Number(ps?.drink_vat ?? bill?.drink_vat ?? bill?.totals?.drink_vat ?? (drinkVatFromItems || drinkVatFromPct || drinkVatFromSettings || 0))
  const foodSubtotalResolved = Number(ps?.food_subtotal ?? bill?.food_subtotal ?? bill?.totals?.food_subtotal ?? foodSubtotalFromItems ?? 0)
  const totalTaxResolved = Number(foodCgst || 0) + Number(foodSgst || 0) + Number(drinkCgst || 0) + Number(drinkSgst || 0) + Number(drinkVat || 0)
  const discountResolved = Number(ps?.food_discount ?? discount ?? 0)
    const grandTotalResolved =
      ps?.grand_total != null
        ? Number(ps.grand_total)
        : Number(
            foodSubtotalResolved +
            drinkSubtotalResolved +
            totalTaxResolved +
            parcelCharge +
            acCharge -
            discountResolved
          ) 
       const handlePrint = async () => {
    if (!bill?.id) { notify.error('Bill not found'); return }
    if (!Array.isArray(bill.items) || bill.items.length === 0) {
      notify.warning('No items found for this bill'); return
    }
    try {
      setPrintingDuplicate(true)
      generateBillPdf({
        type: 'duplicate',
        hotel: HOTEL_INFO,
        bill,
        tableId: bill.table_id,
        items: bill.items || [],
        subtotal,
        discount,
        tax: totalTaxResolved,
        total: grandTotalResolved,
        note: bill.note || '',
        paymentMode: bill.payment_method || 'Cash',
      })
    } catch (error) {
      console.error('Duplicate print failed:', error)
      notify.error(error?.message || 'Failed to print duplicate bill')
    } finally {
      setPrintingDuplicate(false)
    }
  }

  return (
    <Modal
      title="Bill Details"
      open={open}
      onCancel={onClose}
     
      width={760}
      footer={
        <div className="form-actions" style={{ marginTop: 0 }}>
          <Button onClick={onClose} className="secondary-btn">Close</Button>
         {/* <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            loading={printingDuplicate}
            className="primary-btn"
          >
            Duplicate
          </Button> */}
        </div>
      }

      
    >
      <div style={{ marginBottom: 16 }}>
        <div className="permission-module-title">{bill.bill_no || bill.id}</div>
        <div className="permission-module-subtitle">View ordered items and payment summary</div>
      </div>

      <Descriptions column={2} size="middle" bordered>
        <Descriptions.Item label="Bill No">{bill.bill_no || '-'}</Descriptions.Item>
        <Descriptions.Item label="Table">
          {bill.source === 'room_service' ? 'Room Service' : bill.table_name || '-'}
        </Descriptions.Item>
        {bill.source === 'room_service' && (
          <>
            <Descriptions.Item label="Room">{bill.room_number || '-'}</Descriptions.Item>
            <Descriptions.Item label="Guest">{bill.guest_name || '-'}</Descriptions.Item>
          </>
        )}
        <Descriptions.Item label="Payment">
          <Tag color={getPaymentColor(bill.payment_method)}>{bill.payment_method || 'N/A'}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Open Time">
          {formatBillDateTime(bill.created_at)}
        </Descriptions.Item>
        <Descriptions.Item label="Closed Time">
          {formatBillDateTime(bill.closed_at)}
        </Descriptions.Item>
      </Descriptions>

      <Divider />

      {/* ── Items Table ── */}
      <div className="table-card-header" style={{ marginBottom: 12 }}>
        <div>
          <h3>Ordered Items</h3>
          <p>Items included in this bill</p>
        </div>
      </div>

      <div className="common-table-wrap">
        <Table
          dataSource={bill.items || []}
          rowKey={(r) => r.id || `${r.name}-${r.qty}`}
          pagination={false}
          size="middle"
          columns={[
            {
              title: 'Item',
              dataIndex: 'name',
              render: (_, r) => (
                <div>
                  <Text strong>{r.name || r.item_name || '-'}</Text>
                  {r.is_drink === 1 && r.drink_details && (
                    <div style={{ marginTop: 4 }}>
                      <DrinkDetailCell drink_details={r.drink_details} />
                    </div>
                  )}
                </div>
              ),
            },
            {
              title: 'Qty',
              dataIndex: 'qty',
              width: 90,
              align: 'center',
              render: (_, r) =>
                r.is_drink === 1 && r.drink_details
                  ? '-'
                  : r.qty,
            },
            {
              title: 'Price',
              width: 120,
              align: 'right',
              render: (_, r) =>
                r.is_drink === 1 && r.drink_details
                  ? '-'
                  : `₹${Number(r.price || 0).toFixed(2)}`,
            },
            {
              title: 'Total',
              width: 140,
              align: 'right',
              render: (_, r) => {
                if (r.is_drink === 1 && r.drink_details) {
                  const drinkTotal = getDrinkTotal(r.drink_details)
                  return <Text strong>₹{drinkTotal.toFixed(2)}</Text>
                }
                return `₹${(Number(r.price || 0) * Number(r.qty || 0)).toFixed(2)}`
              },
            },
          ]}
          locale={{
            emptyText: (
              <Empty description="No items found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ),
          }}
        />
      </div>

      <Divider />

      {/* ── Payment Summary from payment_summary field ── */}
      <div className="table-card-header" style={{ marginBottom: 12 }}>
        <div>
          <h3>Payment Summary</h3>
          <p>Breakdown from bill payment record</p>
        </div>
      </div>

      {ps || totalTaxResolved > 0 ? (
        <Row gutter={[16, 16]} className="user-stats-row"  style={{alignItems:"stretch",}}>
          {/* Food section */}




          
       <Descriptions
  bordered
  column={3}
  size="middle"
  style={{ marginTop: 12 }}
>
 

{/* FOOD */}
{(
  Number(ps?.food_subtotal ?? foodSubtotalFromItems) > 0 ||
  foodCgst > 0 ||
  foodSgst > 0
) && (
  <>
    <Descriptions.Item label="Food Subtotal">
      ₹{Number(ps?.food_subtotal ?? foodSubtotalFromItems).toFixed(2)}
    </Descriptions.Item>

    {foodCgst > 0 && (
      <Descriptions.Item label="Food CGST">
        ₹{foodCgst.toFixed(2)}
      </Descriptions.Item>
    )}

    {foodSgst > 0 && (
      <Descriptions.Item label="Food SGST">
        ₹{foodSgst.toFixed(2)}
      </Descriptions.Item>
    )}
  </>
)}

{(
  Number(ps?.drink_subtotal ?? drinkSubtotalFromItems) > 0 ||
  drinkCgst > 0 ||
  drinkSgst > 0 ||
  drinkVat > 0
) && (
  <>
    <Descriptions.Item label="Drink Subtotal">
      ₹{Number(ps?.drink_subtotal ?? drinkSubtotalFromItems).toFixed(2)}
    </Descriptions.Item>

     {drinkCgst > 0 && (
      <Descriptions.Item label="Drink CGST">
        ₹{drinkCgst.toFixed(2)}
      </Descriptions.Item>
    )}

    {drinkSgst > 0 && (
      <Descriptions.Item label="Drink SGST">
        ₹{drinkSgst.toFixed(2)}
      </Descriptions.Item>
    )}

    {drinkVat > 0 && (
      <Descriptions.Item label="Drink VAT">
        ₹{Number(drinkVat).toFixed(2)}
      </Descriptions.Item>
    )}
  </>
)}


  {/* DISCOUNT */}
  {discount > 0 && (
    <Descriptions.Item label="Discount">
      ₹{discount.toFixed(2)}
    </Descriptions.Item>
  )}

  {/* TAX */}
  {totalTaxResolved > 0 && (
    <Descriptions.Item label="Tax">
      ₹{totalTaxResolved.toFixed(2)}
    </Descriptions.Item>
  )}
     {parcelCharge > 0 && (
  <Descriptions.Item label="Parcel Charge">
    ₹{parcelCharge.toFixed(2)}
  </Descriptions.Item>
)}

{acCharge > 0 && (
  <Descriptions.Item label="AC Charge">
    ₹{acCharge.toFixed(2)}
  </Descriptions.Item>
)}

  {/* GRAND TOTAL */}
  <Descriptions.Item label="Grand Total" span={2}>
    <Text strong style={{ fontSize: 16 }}>
        {/*₹{grandTotalResolved.toFixed(2)}*/}
      ₹{Math.round(grandTotalResolved).toLocaleString('en-IN')}
    </Text>
  </Descriptions.Item>
</Descriptions>




        </Row>
      ) : (
        // Fallback when payment_summary is null
        <Row gutter={[16, 16]} className="user-stats-row">
          <Descriptions
              bordered
              column={1}
              size="middle"
              style={{ marginTop: 12 }}
            >
              <Descriptions.Item label="Subtotal">
                ₹{subtotal.toFixed(2)}
              </Descriptions.Item>

              <Descriptions.Item label="Discount">
                ₹{discount.toFixed(2)}
              </Descriptions.Item>

              <Descriptions.Item label="Total Tax">
                ₹{totalTaxResolved.toFixed(2)}
              </Descriptions.Item>

              <Descriptions.Item label="Grand Total">
                <Text strong style={{ fontSize: 16 }}>
                  ₹{grandTotalResolved.toFixed(2)}
                </Text>
              </Descriptions.Item>
            </Descriptions>
                    </Row>
                  )}
                </Modal>
              )
            }

// ─── Main Page ───────────────────────────────────────────────────────────────
const BillsHistoryNoContext = () => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useState('today')
  const [search, setSearch] = useState('')
  const [dateRange, setDateRange] = useState(null)
  const [selectedBill, setSelectedBill] = useState(null)
  const [generalSettings, setGeneralSettings] = useState({})

  const fetchBills = async (filters = {}) => {
    try {
      setLoading(true)
      const res = await BillService.getClosedBills(filters)
      console.log('res', res)
      setData(Array.isArray(res) ? res : [])
    } catch (error) {
      console.error('Failed to fetch closed bills:', error)
      setData([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await BillService.getGeneralSettings()
        setGeneralSettings(settings || {})
      } catch (error) {
        console.error('Failed to fetch general settings:', error)
        setGeneralSettings({})
      }
    }
    fetchSettings()
  }, [])

  const handleRefresh = () => {
    if (filter === 'today') fetchBills({ date: 'today' })
    else if (filter === 'yesterday') fetchBills({ date: 'yesterday' })
    else if (filter === 'range' && dateRange?.length === 2)
      fetchBills({ from: dateRange[0].format('YYYY-MM-DD'), to: dateRange[1].format('YYYY-MM-DD') })
  }

  const filtered = useMemo(() => {
    if (!search) return data
    const q = search.toLowerCase()
    return data.filter((b) => {
      return (
        String(b.table_name || '').toLowerCase().includes(q) ||
        String(b.bill_no || '').toLowerCase().includes(q) ||
        String(b.payment_method || '').toLowerCase().includes(q) ||
        String(b.room_number || '').toLowerCase().includes(q) ||
        String(b.guest_name || '').toLowerCase().includes(q)
      )
    })
  }, [data, search])

 const totalRevenue = useMemo(() => {
  return Math.round(
    filtered.reduce((sum, b) => sum + Number(b.final_total || 0), 0)
  )
}, [filtered])



  const totalItems = filtered.reduce(
    (sum, b) => sum + (b.items || []).reduce((s, i) => s + Number(i.qty || 0), 0),
    0
  )

  const columns = [
    {
      title: 'Bill No',
      dataIndex: 'bill_no',
      width: 160,
      render: (value) => <Text code>{value || '-'}</Text>,
    },
    {
      title: 'Table',
      dataIndex: 'table_name',
      render: (value, r) => (
        <Text strong>{r.source === 'room_service' ? 'Room Service' : value || '-'}</Text>
      ),
    },
    {
      title: 'Room',
      dataIndex: 'room_number',
      width: 110,
      render: (value) => (value ? <Text>{value}</Text> : '-'),
    },
    {
      title: 'Items',
      width: 100,
      align: 'center',
      render: (_, r) => <span className="uid-text">{(r.items || []).length}</span>,
    },
    {
      title: 'Payment',
      dataIndex: 'payment_method',
      width: 130,
      render: (value) => <Tag color={getPaymentColor(value)}>{value || 'N/A'}</Tag>,
    },
    
    {
      title: 'Open Time',
      width: 140,
      render: (_, r) => formatBillDateTime(r.created_at, 'hh:mm A'),
    },
    {
      title: 'Close Time',
      width: 140,
      render: (_, r) => formatBillDateTime(r.closed_at, 'hh:mm A'),
    },
    {
      title: 'Action',
      width: 100,
      align: 'center',
      render: (_, r) => (
        <Button
          size="small"
          shape="circle"
          icon={<EyeOutlined />}
          className="table-icon-btn edit-btn"
          onClick={() => setSelectedBill(r)}
        />
      ),
    },
  ]

  return (
    <div className="page-shell user-master-page">
      <div className="user-page-header">
        <div className="user-page-header-left">
          <div className="user-page-icon">
            <FileTextOutlined />
          </div>
          <div>
            <h2 className="user-page-title">Bill History</h2>
            <p className="user-page-subtitle">
              View settled bills, sold items, payment details and revenue summary.
            </p>
          </div>
        </div>
        <div className="user-page-header-actions">
          <Button icon={<ReloadOutlined />} className="secondary-btn" onClick={handleRefresh}>
            Refresh
          </Button>
        </div>
      </div>

      <Row gutter={[16, 16]} className="user-stats-row">
        <Col xs={24} sm={12} lg={8}>
          <div className="stats-card">
            <div className="stats-card-icon blue"><FileTextOutlined /></div>
            <div>
              <div className="stats-card-label">Total Bills</div>
              <div className="stats-card-value">{filtered.length}</div>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <div className="stats-card">
            <div className="stats-card-icon green"><ShoppingCartOutlined /></div>
            <div>
              <div className="stats-card-label">Items Sold</div>
              <div className="stats-card-value">{totalItems}</div>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <div className="stats-card">
            <div className="stats-card-icon red"><WalletOutlined /></div>
            <div>
              <div className="stats-card-label">Revenue</div>
              <div className="stats-card-value">₹{totalRevenue}</div>
            </div>
          </div>
        </Col>
      </Row>

      <div className="page-card filter-card">
        <div className="filter-card-header">
          <h3>Filters</h3>
          <span>{filtered.length} bills found</span>
        </div>
        <div className="filters-inline-row">
          <div className="filter-item user-filter-item">
            <label className="field-label">Period</label>
            <Segmented
              value={filter}
              onChange={setFilter}
              options={[
                { label: 'Today', value: 'today' },
                { label: 'Yesterday', value: 'yesterday' },
                { label: 'Date Range', value: 'range' },
              ]}
              block
            />
          </div>
          {filter === 'range' && (
            <div className="filter-item user-filter-item">
              <label className="field-label">Date Range</label>
              <RangePicker onChange={(vals) => setDateRange(vals)} className="page-search w-full" />
            </div>
          )}
          <div className="filter-item module-search-item">
            <label className="field-label">Search</label>
            <Input
              placeholder="Search bill no, table, room, guest, payment..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              className="page-search"
            />
          </div>
          <div className="filter-item action-btn-item">
            <label className="field-label">&nbsp;</label>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh} className="secondary-btn">
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="page-card table-card">
        <div className="table-card-header">
          <div>
            <h3>Closed Bills</h3>
            <p>View closed bill records with totals and payment details.</p>
          </div>
        </div>
        <div className="common-table-wrap">
          <Spin spinning={loading}>
            <Table
              dataSource={filtered}
              columns={columns}
              rowKey={(r) => r.id || r.bill_no}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              locale={{
                emptyText: (
                  <Empty
                    description="No bills found for selected filter"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
              scroll={{ x: 900 }}
            />
          </Spin>
        </div>
      </div>

      <BillDetailModal
        bill={selectedBill}
        open={!!selectedBill}
        onClose={() => setSelectedBill(null)}
        generalSettings={generalSettings}
      />
    </div>
  )
}

export default BillsHistoryNoContext
